import { useState } from '#imports'
import { type ComputedRef, type Ref, watchEffect } from 'vue'

import type { ModelComparisonCompareProduct } from '@backmarket/http-api/src/api-specs-model-comparison/models/compare-models'

import { getCategorySlug, getComparePairId } from '../../utils/compareTracking'
import type { BreadcrumbItem } from '../useEnrichResultsPage/useEnrichResultsPage.types'

export type CompareContext = {
  page_type: 'compare'
  compare_product_a_model: string
  compare_product_b_model: string
  compare_product_a_id: string
  compare_product_b_id: string
  compare_category: string
  compare_pair_id: string
}

const COMPARE_CONTEXT_STATE_KEY = 'models-comparison.compare-tracking-context'

export function useCompareTrackingContext() {
  return useState<CompareContext | null>(COMPARE_CONTEXT_STATE_KEY, () => null)
}

export function useCompareTracking({
  products,
  category,
  slugA,
  slugB,
}: {
  products: Ref<ModelComparisonCompareProduct[]>
  category: Ref<BreadcrumbItem | undefined>
  slugA: ComputedRef<string>
  slugB: ComputedRef<string>
}) {
  const compareContext = useCompareTrackingContext()

  // Sorted by slug so analytics groups pairs regardless of URL order. Pair id
  // uses route slugs (not products) to stay valid before enrichment loads.
  watchEffect(() => {
    const sortedProducts = [...products.value].sort((p1, p2) =>
      p1.slug.localeCompare(p2.slug),
    )
    const productA = sortedProducts[0]
    const productB = sortedProducts[1]

    compareContext.value = {
      page_type: 'compare',
      compare_product_a_model: productA?.slug ?? '',
      compare_product_b_model: productB?.slug ?? '',
      compare_product_a_id: productA?.productId ?? '',
      compare_product_b_id: productB?.productId ?? '',
      compare_category: getCategorySlug(category.value?.url) ?? '',
      compare_pair_id: getComparePairId(slugA.value, slugB.value),
    }
  })

  function withCompareContext(payload: Record<string, unknown>) {
    return { ...(compareContext.value ?? {}), ...payload }
  }

  return { withCompareContext }
}
